import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { AlertCircle, RotateCcw, MessageSquarePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { recordUnhandledError } from "@/lib/diagnostics";

export default function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
    const [reportedTicket, setReportedTicket] = useState<{ id: string; code: string } | null>(null);

    useEffect(() => {
        let mounted = true;
        recordUnhandledError("boundary", error).then((res) => {
            if (mounted && res?.code) {
                setReportedTicket({ id: res.id, code: res.code });
            }
        });
        return () => {
            mounted = false;
        };
    }, [error]);

    const handleAddDescription = () => {
        window.dispatchEvent(
            new CustomEvent("open-feedback-widget", {
                detail: {
                    feedbackId: reportedTicket?.id,
                    code: reportedTicket?.code,
                    title: error?.message || "Lỗi màn hình ứng dụng",
                },
            }),
        );
    };

    return (
        <div className="flex h-screen flex-col items-center justify-center bg-slate-50 p-4 text-center">
            <div className="bg-rose-100 p-4 rounded-full mb-6 animate-in zoom-in duration-300">
                <AlertCircle className="h-12 w-12 text-rose-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-3">Đã có lỗi xảy ra!</h1>
            <p className="text-slate-500 max-w-md mb-4 leading-relaxed">
                Hệ thống gặp sự cố không mong muốn.
                <br />
                <span className="text-xs font-mono bg-slate-200 px-2 py-1 rounded text-slate-600 mt-2 inline-block">
                    {error.message || "Unknown Error"}
                </span>
            </p>
            {reportedTicket?.code && (
                <p
                    data-testid="error-boundary-auto-ticket"
                    className="text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-lg mb-6"
                >
                    Đã tự động gửi báo lỗi #{reportedTicket.code}
                </p>
            )}
            <div className="flex flex-wrap items-center justify-center gap-3">
                <Button variant="outline" onClick={() => window.location.reload()} className="gap-2">
                    <RotateCcw className="h-4 w-4" /> Tải lại trang
                </Button>
                <Button onClick={reset} className="gap-2 bg-indigo-600 hover:bg-indigo-700">
                    Thử lại
                </Button>
                <Button
                    type="button"
                    variant="outline"
                    data-testid="error-boundary-add-description"
                    onClick={handleAddDescription}
                    className="gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                    <MessageSquarePlus className="h-4 w-4" /> Thêm mô tả
                </Button>
                <Button variant="ghost" asChild>
                    <Link to="/">Về trang chủ</Link>
                </Button>
            </div>
        </div>
    );
}
